import { useEffect, useRef } from 'react';
import { useReducedMotion } from '@/hooks/useReducedMotion';

interface NeuralNetworkCanvasProps {
  className?: string;
  /** Overall opacity of the drawing (default 0.55 — background accent). */
  opacity?: number;
}

interface Node {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  hue: string;
}

// Brand blues from the 2026 logo palette.
const NODE_COLORS = ['#2563eb', '#3b82f6', '#60a5fa', '#1e40af'];
const LINK_COLOR = '37, 99, 235'; // rgb of #2563eb
const LINK_DIST = 130;
const POINTER_DIST = 160;

/**
 * Interactive neural-network background: drifting nodes joined by
 * distance-faded links; on fine pointers, nodes near the cursor connect to it
 * and ease gently toward it. Dependency-free canvas.
 *
 * Etiquette: DPR-capped at 2; the rAF loop runs only while the canvas is in
 * the viewport and the tab is visible. Under prefers-reduced-motion a single
 * static frame is drawn (no animation, content unaffected). Decorative only —
 * aria-hidden, pointer-events none.
 */
const NeuralNetworkCanvas = ({ className, opacity = 0.55 }: NeuralNetworkCanvasProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const reduced = useReducedMotion();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const parent = canvas.parentElement ?? canvas;
    let width = 0;
    let height = 0;
    let nodes: Node[] = [];
    let raf = 0;
    let running = false;
    let inView = true;
    const pointer = { x: -9999, y: -9999, active: false };
    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const seed = () => {
      const count = Math.max(14, Math.min(40, Math.round(width / 34)));
      nodes = Array.from({ length: count }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.25,
        vy: (Math.random() - 0.5) * 0.25,
        r: 1.6 + Math.random() * 2.2,
        hue: NODE_COLORS[Math.floor(Math.random() * NODE_COLORS.length)],
      }));
    };

    const resize = () => {
      const rect = parent.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      seed();
    };

    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      ctx.globalAlpha = opacity;

      // Links between close nodes.
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const d = Math.hypot(dx, dy);
          if (d < LINK_DIST) {
            ctx.strokeStyle = `rgba(${LINK_COLOR}, ${(1 - d / LINK_DIST) * 0.35})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.stroke();
          }
        }
      }

      // Pointer links (fine pointers only).
      if (pointer.active) {
        for (const n of nodes) {
          const d = Math.hypot(n.x - pointer.x, n.y - pointer.y);
          if (d < POINTER_DIST) {
            ctx.strokeStyle = `rgba(${LINK_COLOR}, ${(1 - d / POINTER_DIST) * 0.5})`;
            ctx.lineWidth = 1.2;
            ctx.beginPath();
            ctx.moveTo(n.x, n.y);
            ctx.lineTo(pointer.x, pointer.y);
            ctx.stroke();
          }
        }
      }

      // Nodes.
      for (const n of nodes) {
        ctx.fillStyle = n.hue;
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    };

    const step = () => {
      for (const n of nodes) {
        n.x += n.vx;
        n.y += n.vy;
        // Gentle pointer pull.
        if (pointer.active) {
          const dx = pointer.x - n.x;
          const dy = pointer.y - n.y;
          const d = Math.hypot(dx, dy);
          if (d < POINTER_DIST && d > 1) {
            n.x += (dx / d) * 0.18;
            n.y += (dy / d) * 0.18;
          }
        }
        if (n.x < 0 || n.x > width) n.vx *= -1;
        if (n.y < 0 || n.y > height) n.vy *= -1;
        n.x = Math.max(0, Math.min(width, n.x));
        n.y = Math.max(0, Math.min(height, n.y));
      }
      draw();
      raf = requestAnimationFrame(step);
    };

    const start = () => {
      if (running || reduced) return;
      running = true;
      raf = requestAnimationFrame(step);
    };
    const stop = () => {
      running = false;
      cancelAnimationFrame(raf);
    };

    resize();
    if (reduced) {
      draw(); // single static frame — network visible, nothing moves
    } else {
      start();
    }

    const onResize = () => {
      resize();
      if (reduced) draw();
    };
    window.addEventListener('resize', onResize);

    const onMove = (e: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      pointer.x = e.clientX - rect.left;
      pointer.y = e.clientY - rect.top;
      pointer.active = true;
    };
    const onLeave = () => {
      pointer.active = false;
    };
    if (finePointer && !reduced) {
      parent.addEventListener('pointermove', onMove);
      parent.addEventListener('pointerleave', onLeave);
    }

    // Run only while visible.
    let io: IntersectionObserver | undefined;
    if (typeof IntersectionObserver !== 'undefined' && !reduced) {
      io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            inView = entry.isIntersecting;
            if (inView && !document.hidden) start();
            else stop();
          });
        },
        { threshold: 0.02 },
      );
      io.observe(canvas);
    }
    const onVisibility = () => {
      if (document.hidden) stop();
      else if (inView) start();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      stop();
      window.removeEventListener('resize', onResize);
      parent.removeEventListener('pointermove', onMove);
      parent.removeEventListener('pointerleave', onLeave);
      document.removeEventListener('visibilitychange', onVisibility);
      io?.disconnect();
    };
  }, [reduced, opacity]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      aria-hidden="true"
      style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
    />
  );
};

export default NeuralNetworkCanvas;
